import Deferred from 'es6-deferred'
import d3 from './d3-3'
import {
  every, first, last, zip,
  min, max, map, range,
  fill, get, isNil, isEmpty
} from 'lodash'

// import * as d3Alt from "d3";

// import SVG from 'svg.js';

export function DistributionChart() {
  this.create = function (settings) {
    var deferred = new Deferred()

    this.divId = settings.el
    this.margin = settings.margin
    var parent = d3.select('#' + this.divId)

    // Add class to parent div so that our css selectors can be scoped
    parent.classed('distribution-chart', true)

    var bbox = parent.node().getBoundingClientRect()
    this.width = bbox.width - this.margin.left - this.margin.right
    this.height = bbox.height - this.margin.top - this.margin.bottom

    this.x = d3.scale.linear().range([0, this.width])

    this.y = d3.scale.linear().range([this.height, 0])

    this.svg = parent
      .append('svg')
      .attr('overflow', 'visible')
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom)
      .append('g')
      .attr(
        'transform',
        'translate(' + this.margin.left + ',' + this.margin.top + ')'
      )

    this.xAxisContainer = this.svg
      .append('g')
      .attr('class', 'x axis')
      .attr('font-family', 'GT-America-Mono')
      .attr('font-size', this.width / 22)
      .attr('transform', 'translate(0,' + (this.height - 1) + ')')

    this.yAxisContainer = this.svg.append('g').attr('class', 'y axis')

    this.lineContainer = this.svg
      .append('g')
      .attr('class', 'line')
      .insert('path') // TODO: look into replacing previous content

    this.svg
      .append('text')
      .attr('class', 'interpretation')
      .attr('x', 0)
      .attr('y', -this.height / 2.7)
      .attr('fill', '#000')
      .attr('text-anchor', 'start')
      .attr('font-family', 'GT-America-Thin')
      .attr('font-size', 16)
      .text('')

    // Patient Indicator
    // var filter = this.svg
    //   .append('defs')
    //   .append('filter')
    //   .attr('id', 'blur')
    //   .append('feGaussianBlur')
    //   .attr('stdDeviation', 5)

    // Rect indicator
    this.svg
      .append('rect')
      .attr('class', 'indicator')
      .attr('id', this.divId + '_indicator')
      .attr('x', 0)
      .attr('rx', 3)
      .attr('ry', 3)
      .attr('y', -1) // -1: Account for rounded corners
      .attr('width', 7)
      .attr('height', this.height)
      .attr('fill', '#959595')
      // .attr("filter", "url(#blur)")
      .moveToFront()

    // Outline
    this.svg
      .append('rect')
      .attr('class', 'outline')
      .attr('y', -3) // Don't cover the graph edge
      .attr('width', this.width)
      .attr('height', this.height + 3) // Don't cover the graph edge
      // .style("fill", "none")
      .attr('stroke-width', 2)
      .attr('stroke', '#959595')
      .attr('fill', 'none')

    deferred.resolve('resolved')
    return deferred
  }

  this.update = function (data) {
    this.data = data
    this.interpretation = setOr(data.interpretation, 'Normal')
    this.ranges = setOr(data.ranges, [
      {
        interpretation: this.interpretation,
        color: '#959595',
        min: 0,
        max: 100
      }
    ])

    this.value = setOr(data.value, 0)
    this.plotPoints = setOr(data.density_plot, emptyPlot())

    /* Testing if the plot points came as empty */

    if (every(this.plotPoints, plot => last(plot) === 0)) {
      // console.log('empty plots', '', true)
    }

    const candidates = map(this.plotPoints, plot => first(plot))
    this.min = min(candidates)
    this.max = max(candidates)

    // Deprecated: domain was determined by extent of plot points
    // this.min = this.x.domain()[0];
    // this.max = this.x.domain()[1];

    // x.domain(d3.extent(this.plotPoints, function(d) {
    //   return d[0];
    // }));

    this.x.domain([this.min, this.max])
    this.y.domain(d3.extent(this.plotPoints, plot => last(plot)))

    // Deprecated: calculate gaussian curve. Keeping in case we want to go back to it.
    // this.getData();

    var xAxis = d3.svg
      .axis()
      .scale(this.x)
      .orient('bottom')
      .ticks(5)
      .tickSize(-this.height)
      .tickPadding(this.height / 3)

    var yAxis = d3.svg
      .axis()
      .scale(this.y)
      .orient('left')

    var line = d3.svg
      .line()
      .x(dx => this.x(dx[0]))
      .y(dy => this.y(dy[1]))

    this.xAxisContainer.call(xAxis)

    this.yAxisContainer
      .call(yAxis)
      .select('.ticks')
      .remove()

    // Curve

    this.lineContainer
      .datum(this.plotPoints)
      .transition()
      .attr('d', line)

    // Determine color and pick a range
    const range =
      find(this.ranges, ['interpretation', this.interpretation]) ||
      first(this.ranges)
    // console.log({ range: range })
    const color = get(range, 'color', '#FFFFFF')

    // Clamp value (always show an indicator, even if value is out of chart range)

    let clamped = this.x(this.value)

    if (this.value <= this.min) {
      // console.log('smaller than min', { value: data.value, min: this.min })
      clamped = this.x(this.min) + 2
    }
    if (this.value >= this.max) {
      // console.log('bigger than max', { value: data.value, max: this.max })
      clamped = this.x(this.max) - 10
    }
    // Deprecated; in case we want to determine the current range by label, not value:
    // for (var i = 0; i < this.ranges.length; i++) {
    //   if (this.ranges[i].interpretation == data.interpretation) {
    //     range = this.ranges[i];
    //     color = range.color;
    //   }
    // }

    // Range determined by value
    // for (var i = 0; i < this.ranges.length; i++) {
    //   if (this.ranges[i].min <= data.value && data.value < this.ranges[i].max) {
    //     range = this.ranges[i]
    //   }
    // }

    // log('RANGE', range)

    // if (range) {
    //   // Data is within chart ranges
    //   clamped = x(data.value)
    //   color = get(range, 'color', '#FFFFFF')
    // } else if (data.value <= this.min) {
    //   log('smaller than min', { value: data.value, min: this.min })
    //   // Data is under chart min
    //   clamped = x(this.min) + 2
    //   // Set range to min range
    //   range = first(this.ranges)
    //   color = get(range, 'color', '#FFFFFF')
    // } else if (data.value >= this.max) {
    //   log('bigger than max', { value: data.value, min: this.max })

    //   // Data is over chart max
    //   clamped = x(this.max) - 10
    //   range = last(this.ranges)
    //   color = get(range, 'color', '#FFFFFF')
    // }
    // // console.log('min', this.min)
    // // console.log('max', this.max)
    // // console.log('data', data.value)
    // // console.log('pos', x(data.value))
    // // console.log('clamped pos', clamped)
    this.svg
      .select('#' + this.divId + '_indicator')
      .transition()
      .attr('x', clamped)
      .attr('fill', color)

    this.svg
      .select('.interpretation')
      .text(this.interpretation)
      .transition()
      .attr('fill', color)
  }

  this.randomValue = function () {
    // Pick random range
    var i = Math.floor(Math.random() * this.ranges.length)
    var range = this.ranges[i]

    this.data.interpretation = range.interpretation
    this.data.value = Math.floor(
      range.min + Math.random() * (range.max - range.min)
    )

    this.update(this.data)
  }

  // // Given a chart value, return the corresponding color from the background gradient
  // this.colorAt = function(gradient, value) {
  //   var first;
  //   // Normalize values
  //   var min = this.x.domain()[0];
  //   var max = this.x.domain()[1];
  //   var offset = (value - min) / (max - min);

  //   // Find the left-hand gradient stop
  //   var pos = 0;
  //   var that = this;
  //   for (var i = 0; i < this.stops.length; i++) {
  //     if (this.stops[i].attr("offset") <= offset) {
  //       first = this.stops[i];
  //       pos = i;
  //     }
  //   }

  //   // Find the right-hand gradient stop
  //   if (pos < this.stops.length - 1) {
  //     last = this.stops[pos + 1];
  //   } else {
  //     last = first;
  //   }

  //   //TODO: Uses SVG.js utility functions;
  //   // In next iteration replace with a custom interpolating function (or d3's linear scales)
  //   var blend, relative, fo, lo;

  //   var c = "#FFFFFF"; //by default, return white
  //   if(first){

  //     // Create morph ––a range of colors
  //     blend = new SVG.Color(first.attr('stop-color'));
  //     blend.morph(last.attr('stop-color'));

  //     // Calculate relative offset
  //     fo = first.attr('offset');
  //     lo = last.attr('offset');
  //     relative = (offset - fo) / (lo - fo);

  //     // Interpolate
  //     c = blend.at(relative).toHex();
  //   }
  //   return c;

  // }

  // Deprecated: Calculate curve based on sample values
  // We now use density plots
  // Keeping this code just in case we need it in the future
  // this.getData = function(){
  //     //Set sample size
  //     var num = 100000;
  //     if(this.dist)
  //         num = this.plotPoints.length;

  //     for (var i = 0; i < num.length; i++) {
  //         //If actual sample data available, use it:
  //         if(this.distribution){
  //             q = this.distribution[i];
  //         }
  //         else{
  //             // console.log("warning: because computed data not available, using normal distribution generation. This may cause performance problems")
  //             q = d3.randomNormal(this.stats.mean, stats.standard_deviation)()
  //         }

  //             p = this.gaussian(q, this.stats.mean, stats.standard_deviation)
  //             el = {
  //                 "q": q,
  //                 "p": p
  //             }
  //         this.data.push(el)
  //     };

  //     need to sort for plotting
  //     https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
  //     this.data.sort(function(x, y) {
  //         return x.q - y.q;
  //     });
  // }

  // this.gaussian = function(x, mean, sigma){
  //     var gaussianConstant = 1 / Math.sqrt(2 * Math.PI);
  //     x = (x - mean) / sigma;
  //     return gaussianConstant * Math.exp(-.5 * x * x) / sigma;
  // }

  // this.kernelDensityEstimator = function(kernel, x) {
  //   return function(sample) {
  //     return x.map(function(x) {
  //         return { "q": x, "p": d3.mean(sample, function(v) { return kernel(x - v); })};
  //         // return [x, d3.mean(sample, function(v) { return kernel(x - v); })];
  //     });
  //   };
  // }

  // this.epanechnikovKernel = function(bandwith) {
  //   return function(u) {
  //     //return Math.abs(u /= bandwith) <= 1 ? .75 * (1 - u * u) / bandwith : 0;
  //     if(Math.abs(u = u /  bandwith) <= 1) {
  //      return 0.75 * (1 - u * u) / bandwith;
  //     } else return 0;
  //   };
  // }
}

d3.selection.prototype.moveToFront = function () {
  return this.each(function () {
    this.parentNode.appendChild(this)
  })
}

d3.selection.prototype.moveToBack = function () {
  return this.each(function () {
    var firstChild = this.parentNode.firstChild
    if (firstChild) {
      this.parentNode.insertBefore(this, firstChild)
    }
  })
}

function emptyPlot(min = 0, max = 100) {
  const x = range(min, max, (max - min) / 200)
  const y = fill(Array(200), 0)

  return zip(x, y)
}

function setOr(value, fallback) {
  if (isNil(value) && isEmpty(value)) {
    console.error('NO DATA!!')
    return fallback
  }
  return value
}
